import { MeetingFragmentFragment } from '@lopo-tech/lopo-api'
import React from 'react'
import { Button } from 'src/components/button/button'
import Icon from 'src/components/forms/icon/icon'
import { SvgIcon } from 'src/components/icons/svg-icon'
import StaticMap from 'src/components/post/shared/ingredients/map/static_map'
import { CLASS_PREFIX } from 'src/constants'
import { MAP_DEFAULT_ZOOM } from 'src/constants/map'
import { textResources } from 'src/lang/de'
import { COLOR_TYPE_GRAY_TINT_14 } from 'src/utils/color'
import DateFormatter from 'src/utils/date_formatter/date_formatter'
import { DetailCard } from '../project_detail/detail_card/detail_card'
import './meeting_tile.scss'

const t = textResources

type Props = {
  meeting: MeetingFragmentFragment;
  setMeetingForReport: (meeting: MeetingFragmentFragment) => void;
  onEdit?: () => void;
  withPadding?: boolean;
};

const cls = CLASS_PREFIX + 'meeting-tile'

const formatAddress = (meeting: MeetingFragmentFragment): string => {
  if (!meeting.address) {
    return ''
  }

  const parts = [meeting.address.street, meeting.address.city].filter(Boolean)

  return parts.join(', ')
}

// we cannot access the SCSS variables here unfortunatly, so we need to duplicate the color values
const markerColor = '323232'

export const MeetingTile: React.FC<Props> = ({
  meeting,
  onEdit,
  setMeetingForReport,
  withPadding = true
}) => {
  const addressString = formatAddress(meeting)

  return (
    <DetailCard key={meeting.id} withPadding={withPadding}>
      <div className={`${cls}__header`}>
        <div className={`${cls}__date`}>
          <span className={`${cls}__date-item`}>
            <Icon name="calendar" size={18} color={COLOR_TYPE_GRAY_TINT_14} />
            <span>
              {DateFormatter.asDayMonthYearLong(meeting.startTime)}{' '}
              {DateFormatter.asTime(meeting.startTime)} –{' '}
              {DateFormatter.asTime(meeting.endTime)}
            </span>
          </span>
        </div>

        <div className={`${cls}__actions-desktop`}>
          {meeting.canUpdate.value && onEdit && (
            <Button
              variant="secondary"
              onClick={() => {
                onEdit?.()
              }}
              title={t.projects.details.updateMeeting}
            />
          )}
          {meeting.report && (
            <Button
              variant="secondary"
              onClick={() => setMeetingForReport(meeting)}
              title={t.projects.meetingReport.viewReport}
              iconLeft={
                <div className={`${cls}__button-icon`}>
                  <SvgIcon name="Files/file-02" />
                </div>
              }
            />
          )}
          {!meeting.report && meeting.canCreateReport.value && (
            <Button
              onClick={() => setMeetingForReport(meeting)}
              title={t.projects.meetingReport.createReport}
              iconLeft={
                <div className={`${cls}__button-icon`}>
                  <SvgIcon name="Files/file-02" />
                </div>
              }
            />
          )}
        </div>
      </div>
      <h3 className={`${cls}__title`}>{meeting.title}</h3>
      {meeting.description && (
        <p className={`${cls}__content`}>{meeting.description}</p>
      )}
      <div className={`${cls}__divider`} />
      <div className={`${cls}__location-info`}>
        {meeting.onlineMeetingUrl && (
          <p>
            <span>{t.projects.createMeetingModal.meetingLink}: </span>
            <a
              href={meeting.onlineMeetingUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {meeting.onlineMeetingUrl}
            </a>
          </p>
        )}
        {meeting.address && (
          <div className={`${cls}__map`}>
            <StaticMap
              position={meeting.address?.coordinates}
              zoom={MAP_DEFAULT_ZOOM}
              markerColor={markerColor}
              address={addressString}
            />
            {addressString && (
              <p className={cls + '__address'}>
                <Icon size={16} name="marker" className={cls + '__icon'} />
                {addressString}
              </p>
            )}
          </div>
        )}
        {meeting.locationDescription && (
          <p className={`${cls}__content`}>{meeting.locationDescription}</p>
        )}
        <div className={`${cls}__actions-mobile`}>
          {meeting.canUpdate.value && onEdit && (
            <Button
              variant="secondary"
              onClick={() => {
                onEdit?.()
              }}
              title={t.projects.details.updateMeeting}
            />
          )}
          {meeting.report && (
            <Button
              variant="secondary"
              onClick={() => setMeetingForReport(meeting)}
              title={t.projects.meetingReport.viewReport}
              iconLeft={
                <div className={`${cls}__button-icon`}>
                  <SvgIcon name="Files/file-02" />
                </div>
              }
            />
          )}
          {!meeting.report && meeting.canCreateReport.value && (
            <Button
              onClick={() => setMeetingForReport(meeting)}
              title={t.projects.meetingReport.createReport}
              iconLeft={
                <div className={`${cls}__button-icon`}>
                  <SvgIcon name="Files/file-02" />
                </div>
              }
            />
          )}
        </div>
      </div>
    </DetailCard>
  )
}
